import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import {
  ColumnBase,
  DataStateChangeEvent,
  FilterService,
  GridComponent,
  GridDataResult,
} from '@progress/kendo-angular-grid';
import { GetUser, KendoGridFilters } from '../../../awsAppSync/API';
import {
  ADD_MOBILTEX_MEMBER,
  DEFAULTPAGESIZE,
  MOBILTEXMEMBERS,
  SEARCHMTXMEMBERS,
  SURVEYOVERVIEW_ELLIPSIS_WIDTH,
  ZERO,
  MOBILTEX_MEMBER_SCREEN,
  FULLACCESS,
  DEFAULTPAGENUMBER,
} from '../../shared/constants';
import { MatDialog } from '@angular/material/dialog';
import { UserService } from '../../core/user.service';
import {
  CompositeFilterDescriptor,
  FilterDescriptor,
  State,
} from '@progress/kendo-data-query';
import {
  RemoveMTX_Member,
  SetMTX_Member,
} from '../../core/store/MTX_Member.state';
import { PermissionService } from '../../core/services/permission.service';
import { LastEvaluatedKeyModel } from '../../core/models/lastevaluatedkey';
import { HelperService } from '../../shared/service/helper/helper.service';
import { Observable } from 'rxjs';
import { ScreenPageSizeState } from '../../core/store/screen-pageSize.state';
import { AuthenticateUserState } from '../../core/store/authenticate-user.state';
import { UserProfileDataState } from '../../core/store/user-profile.state';
import { SetEditPermission } from '../../core/store/edit-permission.state';
import { User } from '../../core/models/user.module';
import { FeatureFlagService } from '../../core/services/feature-flag.service';

@Component({
  selector: 'app-mtx-member-overview',
  templateUrl: './mtx-member-overview.component.html',
  styleUrl: './mtx-member-overview.component.css',
})
export class MTXMemberOverviewComponent implements OnInit {
  @ViewChild('grid') grid!: GridComponent;
  @ViewChild('customFilterContainer', { static: false })
  customFilter!: ElementRef;
  public gridData: GridDataResult = { data: [], total: 0 };
  public searchText = '';
  kendoGridFilters: KendoGridFilters = {};
  public pageSize = DEFAULTPAGESIZE;
  public searchPlaceholder = SEARCHMTXMEMBERS;
  public originalData: any[] = [];
  public inputScreen = MOBILTEXMEMBERS;
  public rowId = ZERO;
  ellipsisWidth = SURVEYOVERVIEW_ELLIPSIS_WIDTH;
  currentDataItem: any;
  dropdownVisible = false;
  dropdownX = ZERO;
  dropdownY = ZERO;
  selectedRowData: any;
  isStickyRow = false;
  public myStickyRows: string[] = [];
  currentPage = ZERO;
  public isLoading = true;
  cityFilterValues: any[] = [];
  stateProvinceFilterValues: any[] = [];
  countryFilterValues: any[] = [];
  accountStatusFilterValues: any[] = [];
  mfaStatusFilterValues: any[] = [];
  // Gates the MFA Status column (SYRC-6098) behind the corinsights_auth0_enabled flag.
  isMfaStatusColumnEnabled = false;
  permission = false;
  roleNameFilterValues: any[] = [];
  lastEvaluatedKey: LastEvaluatedKeyModel | null = null;
  getPageSize$: Observable<(screenName: string) => number | null>;
  loggedInCorViewUserId$: Observable<string | null | undefined> | undefined;
  loggedInCorViewUserId = '';
  isMTXAdmin$: Observable<boolean | null | undefined> | undefined;
  isMTXAdmin = false;
  isMTXDevOps$: Observable<boolean | null | undefined> | undefined;
  isMTXDevOps = false;
  isMTXUserType$: Observable<boolean | null | undefined> | undefined;
  isMTXUserType = false;
  authenticateUserStatePrimaryCompanyId$: Observable<string | null>;
  authenticateUserStatePrimaryCompanyId = '';
  constructor(
    private userService: UserService,
    public dialog: MatDialog,
    private router: Router,
    private store: Store,
    private cdr: ChangeDetectorRef,
    public permissionService: PermissionService,
    public helperService: HelperService,
    private featureFlagService: FeatureFlagService,
    private readonly destroyRef: DestroyRef,
  ) {
    this.loggedInCorViewUserId$ = this.store.select(
      AuthenticateUserState.getLoggedInCorViewUserId,
    );
    this.isMTXAdmin$ = this.store.select(UserProfileDataState.getIsMTXUser);
    this.isMTXDevOps$ = this.store.select(
      UserProfileDataState.getIsMTXDevOpsUser,
    );
    this.isMTXUserType$ = this.store.select(
      UserProfileDataState.getIsMTXUserType,
    );
    this.authenticateUserStatePrimaryCompanyId$ = this.store.select(
      AuthenticateUserState.getSyrcPrimaryCompanyId,
    );
    this.getPageSize$ = this.store.select(ScreenPageSizeState.getPageSize);
  }

  ngOnInit() {
    this.permission = this.permissionService.hasPermission(
      MOBILTEX_MEMBER_SCREEN,
      FULLACCESS,
    );
    // MFA status column is gated behind the Auth0 kill-switch (MFA is only meaningful
    // when Auth0 is the IdP).
    this.featureFlagService
      .isAuth0Enabled$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isEnabled) => {
        this.isMfaStatusColumnEnabled = isEnabled;
      });
    this.isMTXAdmin$?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this.isMTXAdmin = value ?? false;
    });
    this.isMTXDevOps$?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this.isMTXDevOps = value ?? false;
    });
    this.isMTXUserType$?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this.isMTXUserType = value ?? false;
    });
    this.loggedInCorViewUserId$?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this.loggedInCorViewUserId = value ?? '';
    });
    this.getPageSize$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((pageSize) => {
      this.pageSize = pageSize(MOBILTEX_MEMBER_SCREEN) ?? DEFAULTPAGESIZE;
    });
    this.authenticateUserStatePrimaryCompanyId$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (syrcPrimaryCompanyId) => {
        this.authenticateUserStatePrimaryCompanyId = syrcPrimaryCompanyId ?? '';
      },
    );
    this.size = this.pageSize;
    this.getUSerGridData();
  }

  async getUSerGridData(): Promise<void> {
    try {
      this.isLoading = true;
      //modify kendo gridfilter here with aor condition
      const modifiedfilter = this.helperService.modifyFilters(
        this.kendoGridFilters as CompositeFilterDescriptor,
      ) as KendoGridFilters;

      const userData = await this.userService.getAllUser({
        pageNumber: this.currentPage,
        pageSize: this.pageSize ?? 50,
        gridFilters: modifiedfilter,
        searchText: this.searchText,
        isMTXAdmin: true,
        lastEvaluatedKey: this.currentPage == 1 ? null : this.nextKey,
        syrcPrimaryCompanyId: null,
      });

      if (userData?.items) {
        const items = userData.items.filter(
          (item): item is GetUser => item !== null,
        );
        const userModels: User[] = this.mapToUserModules(items);
        this.gridData = {
          data: userModels,
          total: userData?.totalCount != null ? userData?.totalCount : 0,
        };

        if (userData.lastEvaluatedKey != null) {
          if (userData.lastEvaluatedKey.pk != null) {
            this.lastEvaluatedKey = {
              pk: userData?.lastEvaluatedKey?.pk,
              sk: userData?.lastEvaluatedKey?.sk,
            };
          } else {
            this.lastEvaluatedKey = null;
          }
        } else {
          this.lastEvaluatedKey = null;
        }
        this.nextKey = this.lastEvaluatedKey;
        this.cdr.markForCheck();
        this.cdr.detectChanges();
        this.originalData = this.gridData.data;
        this.getDistinctValues('addrCity');
        this.getDistinctValues('stateProvince');
        this.getDistinctValues('country');
        this.getDistinctValues('accountStatus');
        if (this.isMfaStatusColumnEnabled) {
          this.getDistinctValues('mfaStatus');
        }
        this.cdr.markForCheck();
        this.getDistinctValues('userRoleNames');
        this.cdr.detectChanges();
        this.isLoading = false;
      }
    } catch (error) {
      console.error('Error fetching User data', error);
    } finally {
      this.isLoading = false;
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    }
  }

  mapToUserModules(userList: GetUser[]): User[] {
    const selectedPrimaryCompanyId = this.authenticateUserStatePrimaryCompanyId;

    return userList.map((user) => {
      const selectedSyrcCompany =
        user.syrcPrimaryCompany?.find(
          (c) => c?.id === selectedPrimaryCompanyId,
        ) ?? user.syrcPrimaryCompany?.[0];

      const selectedCoreViewCompany =
        user.coreViewPrimaryCompany?.find(
          (c) => c?.id === selectedPrimaryCompanyId,
        ) ?? user.coreViewPrimaryCompany?.[0];

      return {
        __typename: 'User',
        id: user.id ?? '',
        firstName: user.firstName ?? '',
        lastName: user.lastName ?? '',

        syrcPrimaryCompany: {
          __typename: 'UserPrimaryCompany',
          id: selectedSyrcCompany?.id ?? null,
          displayName: selectedSyrcCompany?.displayName ?? null,
        },

        coreViewPrimaryCompany: {
          __typename: 'UserPrimaryCompany',
          id: selectedCoreViewCompany?.id ?? null,
          displayName: selectedCoreViewCompany?.displayName ?? null,
        },

        active: user.active,
        username: user.username ?? '',
        email: user.email ?? '',
        postalZip: user.postalZip ?? null,
        addr1: user.addr1 ?? null,
        addrCity: user.addrCity ?? null,
        cellPhone: user.cellPhone ?? null,
        officePhone: user.officePhone,
        stateProvince: user.stateProvince ?? null,
        country: user.country ?? null,
        timeZoneId: user.timeZoneId ?? null,
        timeZone: user.timeZone ?? null,
        coreViewUserId: user.coreViewUserId ?? null,
        entityType: user.entityType ?? '',
        createdBy: user.createdBy ?? null,
        createdDate: user.createdDate ?? null,
        updatedBy: user.updatedBy ?? null,
        updatedDate: user.updatedDate ?? null,
        isDeleted: user.isDeleted,

        userRoleDisplayNames:
          user.userRoles
            ?.map((x) => x?.name)
            .filter((name) => name != null)
            .join(', ') ?? '',

        accountStatus: user.accountStatus ?? '',
        mfaStatus: user.mfaStatus ?? 'N/A',

        primaryCompanyName: selectedSyrcCompany?.displayName ?? null,

        userRoleNames:
          user.userRoles
            ?.map((x) => x?.name)
            .filter((name) => name != null)
            .join(', ') ?? '',

        level3DisplayNames:
          user.userRoles
            ?.map((x) => x?.level3Value?.level3ValueName)
            .filter((name) => name != null)
            .join(', ') ?? '',
      };
    });
  }

  createUser() {
    this.store.dispatch(new RemoveMTX_Member());
    this.store.dispatch(
      new SetEditPermission(false, false, false, false, false),
    );
    this.router.navigate([ADD_MOBILTEX_MEMBER]);
  }

  getPriorityClass(priority: boolean): string {
    switch (priority) {
      case true:
        return 'high-priority';
      case false:
        return 'medium-priority';
      default:
        return '';
    }
  }

  // Maps mfaStatus to its badge CSS class. Used as a plain lookup in the template
  // (mfaStatusClass[value] ?? mfaStatusClass['Default']) so binding is a property access,
  // not a per-CD method call. Any value not in the map (including "N/A") falls back to Default.
  readonly mfaStatusClass: {
    readonly Default: string;
    readonly [key: string]: string | undefined;
  } = {
    Enabled: 'mfa-badge mfa-badge-enabled',
    Disabled: 'mfa-badge mfa-badge-disabled',
    Default: 'mfa-badge mfa-badge-na',
  };

  openDropdownMenu(event: MouseEvent, dataItem: any) {
    event.stopPropagation();
    this.dropdownVisible = true;
    this.currentDataItem = dataItem;
    this.rowId = this.currentDataItem.id;
    const gridElement = document.querySelector('.k-grid') as HTMLElement;
    const dropdownHeight = 100;
    const dropdownWidth = 200;

    if (gridElement) {
      const gridRect = gridElement.getBoundingClientRect();
      const clickX = event.clientX - gridRect.left + gridElement.scrollLeft;
      const clickY = event.clientY - gridRect.top + gridElement.scrollTop;

      const spaceBelow =
        window.innerHeight -
        (event.clientY - gridRect.top + gridElement.scrollTop);

      if (spaceBelow < dropdownHeight) {
        this.dropdownY = clickY - dropdownHeight;
      } else {
        this.dropdownY = clickY;
      }
      this.dropdownX = Math.max(
        0,
        Math.min(clickX, gridElement.clientWidth - dropdownWidth),
      );

      this.dropdownX += gridRect.left;
      this.dropdownY = Math.max(0, this.dropdownY + 150);
      if (this.dropdownY + dropdownHeight > window.innerHeight) {
        this.dropdownY = window.innerHeight - dropdownHeight;
      }
    }
    this.selectedRowData = dataItem;

    if (this.selectedRowData.coreViewUserId === this.loggedInCorViewUserId) {
      this.store.dispatch(
        new SetEditPermission(false, true, false, false, true),
      );
    } else {
      this.store.dispatch(
        new SetEditPermission(false, false, false, false, false),
      );
    }
  }

  handleSearchInput(changeWord: string) {
    const trimmed = changeWord.trim().toLowerCase();

    if (!trimmed) {
      this.searchText = '';
      this.getUSerGridData();
      return;
    }

    if (trimmed.length > 2) {
      this.searchText = trimmed;
      this.getUSerGridData();
    }
  }

  containsIgnoreCase(field: any, searchTerm: string): boolean {
    if (typeof field === 'string') {
      return field.toLowerCase().includes(searchTerm);
    }
    return false;
  }

  handleDropdownVisibilityChange(visible: boolean) {
    this.dropdownVisible = visible;
  }

  getColumnField(column: ColumnBase): string | null {
    if ('field' in column) {
      return (column as any).field;
    }
    return null;
  }

  getDistinctValues(columnName: keyof User): void {
    const distinctValues = new Set<string>();
    this.gridData.data.forEach((item) => {
      const columnValue = item![columnName];

      // If columnValue is a comma-separated string, split it into individual values
      if (
        (columnName === 'userRoleNames' ||
          columnName === 'level3DisplayNames') &&
        typeof columnValue === 'string'
      ) {
        columnValue.split(',').forEach((role) => {
          if (role.trim()) {
            distinctValues.add(role.trim());
          }
        });
      } else if (
        columnValue !== null &&
        columnValue !== undefined &&
        columnValue !== ''
      ) {
        distinctValues.add(columnValue);
      }
    });
    const filterValuesMap: Record<
      string,
      { text: string; selected: boolean }[]
    > = {
      city: this.cityFilterValues,
      stateProvince: this.stateProvinceFilterValues,
      country: this.countryFilterValues,
      accountStatus: this.accountStatusFilterValues,
      mfaStatus: this.mfaStatusFilterValues,
      userRoleNames: this.roleNameFilterValues,
    };

    // Add distinct values to the respective filter array based on the column name
    distinctValues.forEach((item) => {
      const filterItem = { text: item, selected: false };
      const filterArray = filterValuesMap[columnName];
      if (filterArray) {
        // Check if the item already exists to prevent duplicates
        if (!filterArray.some((f) => f.text === item)) {
          filterArray.push(filterItem);
        }
      }
    });
  }

  public applyFilter(
    selectedValues: any[],
    filterService: FilterService,
    field: string,
  ): void {
    const filters: FilterDescriptor[] = [];
    for (const value of selectedValues) {
      if (value.selected) {
        filters.push({
          field: field,
          operator: 'eq',
          value: value.text,
        });
      }
    }
    const rootFilter: CompositeFilterDescriptor = {
      logic: 'or',
      filters: filters,
    };

    filterService.filter(rootFilter);
  }

  handleDropdownOption(option: number) {
    const isUserSame =
      this.selectedRowData.coreViewUserId === this.loggedInCorViewUserId
        ? true
        : false;
    if (option === 2) {
      this.store.dispatch(
        new SetMTX_Member(true, this.selectedRowData.coreViewUserId),
      );

      if (isUserSame && this.isMTXDevOps) {
        this.store.dispatch(
          new SetEditPermission(false, false, false, true, false),
        );
      } else if (isUserSame && this.isMTXUserType) {
        this.store.dispatch(
          new SetEditPermission(false, false, true, true, false),
        );
      } else {
        this.store.dispatch(
          new SetEditPermission(false, false, false, false, false),
        );
      }
      this.router.navigate([ADD_MOBILTEX_MEMBER]);
    }
  }

  // This code is for Grid Filters and Pagination

  customstate: { filter: CompositeFilterDescriptor } = {
    filter: { logic: 'and', filters: [] },
  };

  public state: State = {
    skip: 0,
    take: this.pageSize,
    group: [],
    filter: { filters: [], logic: 'and' },
    sort: [],
  };

  public dataStateChange(state: DataStateChangeEvent): void {
    this.isLoading = true;
    this.state = state;
    this.currentPage = state.skip / this.pageSize + 1;
    this.kendoGridFilters = this.state.filter as KendoGridFilters;
    const filter = state.filter as CompositeFilterDescriptor;
    this.customstate = { ...this.customstate, filter };
    this.nextKey = null;
    this.currentPage = 1;
    this.pageNumber = 1;
    this.pageLoad = !this.pageLoad;
    this.cdr.detectChanges();
    this.getUSerGridData();
  }

  // Code for custom pagination
  public nextKey: LastEvaluatedKeyModel | null = null;
  public previousKey: LastEvaluatedKeyModel | null = null;
  public size = DEFAULTPAGESIZE; // default page size
  public pageNumber = DEFAULTPAGENUMBER;
  public pageLoad = false;

  onCustomPageChanged(event: {
    page: number;
    pageSize: number;
    nextKey: LastEvaluatedKeyModel | null;
  }) {
    this.helperService.setPageSize(MOBILTEX_MEMBER_SCREEN, event.pageSize);
    if (this.size !== event.pageSize) {
      this.size = event.pageSize;
      this.pageSize = event.pageSize;
      this.pageNumber = 1;
      this.currentPage = 1;
      this.nextKey = null;
      this.getUSerGridData();
    }
    // Handle page number change separately
    else if (this.currentPage !== event.page) {
      this.pageNumber = event.page;
      this.currentPage = event.page;
      this.nextKey = event.nextKey;
      this.getUSerGridData();
    }
  }
}
